/**
 * Browser observability — console ring buffer + uncaught-error capture +
 * batched POST to the api. Inspired by jp-game's consoleCapture.
 *
 * What this collects:
 *   - All console.* calls (log/info/warn/error/debug) — wraps the originals
 *     and tees into a 200-entry ring buffer
 *   - window.error  → captured as 'uncaught'
 *   - window.unhandledrejection → captured as 'unhandledrejection'
 *
 * What it does with them:
 *   - Always: stored in the in-memory ring buffer; available via getLogs()
 *     for the bug-report widget
 *   - When startStreaming(sessionId) has been called: also batches every 5s
 *     to POST /api/system/client-events. Immediate flush on error level.
 *
 * NOT a Sentry replacement — Sentry handles prod crash dashboarding +
 * source maps. This is for "show me what was happening right before the
 * problem" via in-house logs.
 */

import { msalInstance, apiRequest } from '@/auth/msal';
import { CLIENT_DEVICE_HEADER, clientDeviceHeader } from './device-info';
import { isCancelledByUnload, pageUnload } from './page-unload';

const MAX_ENTRIES = 200;
const FLUSH_INTERVAL_MS = 5_000;
const PENDING_CAP = 400;
/**
 * Hard cap on one POST. The server accepts 8 MB (`system-monitor.ts`
 * bodyLimit); 6 MB leaves room for the JSON envelope and headers.
 *
 * ⚠️ `PENDING_CAP` bounds the batch by COUNT, which is not the same thing and
 * was not enough: 400 buffered entries can exceed 8 MB together, and a
 * DOM-snapshot entry is up to 250 KB on its own. A flush posted the whole
 * queue in one request (`pending.splice(0, pending.length)`), so a long-lived
 * admin session could build a body the server refuses — observed on preview
 * 2026-09-12 15:12:23 as a 413 `FST_ERR_CTP_BODY_TOO_LARGE`, rejected by
 * Fastify's body parser before any of our code ran. A 413 is not retryable, so
 * the WHOLE batch was dropped.
 *
 * ⚠️ Past this cap, entries are DISCARDED rather than split across several
 * POSTs (Craig, 2026-09-18). That is a deliberate trade: one bounded request
 * instead of many, at the cost of losing the oldest diagnostics. The
 * alternative — chunking — was built and then removed on that instruction.
 *
 * The NEWEST entries survive, because they describe what just went wrong.
 * Bug reports survive ahead of everything else, at any age or position.
 */
const MAX_POST_BYTES = 6 * 1024 * 1024;
/**
 * How stale a diagnostic entry may be and still be worth sending.
 *
 * A console line from 40 minutes ago says very little about what an operator
 * is looking at now, and it is taking room from what just happened. Dropping
 * it shrinks the batch for a real reason rather than by raising a limit.
 *
 * 30 minutes (Craig, 2026-09-18): long enough to cover a slow-burning problem
 * someone is still investigating, short enough to keep batches small.
 */
const MAX_ENTRY_AGE_MS = 30 * 60 * 1000;

/**
 * Entry types the age filter must NEVER drop.
 *
 * ⚠️ A person TYPED a bug report. If their tab sat idle before the flush
 * succeeded, ageing it out would discard the single highest-signal thing this
 * layer ever carries — and it would vanish with no trace, which is exactly the
 * loss the batching fix exists to prevent. A stale report is still a report.
 *
 * The snapshot rides along because it is useless on its own and its parent is
 * useless without it.
 */
const NEVER_STALE = ['bug_report', 'bug_report_snapshot'] as const;
const ENDPOINT = '/api/system/client-events';

/**
 * Acquire a bearer token for the bug-report POST. Inlined (rather than
 * importing app-kit/request-service) to keep observability layer-free.
 * Returns null if no signed-in account or token acquisition fails — the caller
 * sends the request anyway and the api 401s, which is RETRYABLE: the entry goes
 * back on the pending queue and the next flush (5s) usually has a token. This is
 * the normal page-load race, not an error state.
 */
async function acquireBearer(): Promise<string | null> {
  try {
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) return null;
    const result = await msalInstance.acquireTokenSilent({ ...apiRequest, account });
    return result.accessToken;
  } catch {
    return null;
  }
}

export type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

export interface ConsoleEntry {
  type:  string;     // 'console.log' | 'uncaught' | 'unhandledrejection' | 'bug_report' | 'dom_snapshot'
  level: LogLevel;
  ts:    number;     // epoch ms (browser time)
  message: string;
  payload?: unknown; // stack / dom_html / viewport / etc.
}

const buffer: ConsoleEntry[] = [];
const pending: ConsoleEntry[] = [];
let installed = false;

let session: { id: string; getToken?: () => Promise<string | null> } | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let flushInFlight = false;
const SESSION_ID = makeSessionId();

function makeSessionId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function formatArgs(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack ?? ''}`;
      try { return JSON.stringify(a); } catch { return String(a); }
    })
    .join(' ');
}

/**
 * Vite's dev-server chatter, dropped before it reaches the buffer.
 *
 * ⚠️ This is a LOCAL-ONLY problem, and that is the point rather than a caveat.
 * Deployed environments serve a static `vite build` output from admin-api
 * (`apps/admin-api/Dockerfile`) — no vite process exists there, so no deployed
 * report can contain these lines. The fix is for the environment where triage
 * actually happens.
 *
 * Why it earns a filter anyway: `buffer` is capped at MAX_ENTRIES (200) and is
 * the whole source of a report's `recentLogs`. Measured on local 2026-08-27,
 * **4,109 of 4,360 captured rows — 94% — were `[vite]` lines**, led by 872
 * `hot updated: /src/styles.css`. Every one of those evicts a real log line, so
 * local reports arrived carrying 38 and 55 entries of near-zero value.
 *
 * ⚠️ `Failed to reload` is deliberately KEPT. It is the one vite message that
 * explains an app symptom: it appears seconds before the `ReferenceError:
 * <name> is not defined` render crashes that dominate local reports, and
 * without it those crashes read as app defects rather than mid-edit HMR
 * breakage. Dropping the noise must not drop the explanation.
 */
function isViteNoise(entry: ConsoleEntry): boolean {
  const m = entry.message;
  if (!m.startsWith('[vite]')) return false;
  return !m.includes('Failed to reload');
}

function push(entry: ConsoleEntry) {
  if (isViteNoise(entry)) return;
  buffer.push(entry);
  while (buffer.length > MAX_ENTRIES) buffer.shift();
  if (session) {
    pending.push(entry);
    while (pending.length > PENDING_CAP) pending.shift();
    if (entry.level === 'error') void flushNow();
  }
}

/** Install console wrappers + window error listeners. Idempotent. */
export function installConsoleCapture(): void {
  if (installed) return;
  installed = true;

  const levels: LogLevel[] = ['log', 'info', 'warn', 'error', 'debug'];
  for (const lvl of levels) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const original = (console as any)[lvl].bind(console);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (console as any)[lvl] = (...args: unknown[]) => {
      push({ type: `console.${lvl}`, level: lvl, ts: Date.now(), message: formatArgs(args) });
      original(...args);
    };
  }

  window.addEventListener('error', (e) => {
    autoBugReport('uncaught', e.message ?? '(no message)', {
      filename: e.filename, lineno: e.lineno, colno: e.colno,
      stack: e.error instanceof Error ? e.error.stack : null,
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    // A reload or a closed tab cancelled a fetch nobody awaited (page-unload.ts).
    if (isCancelledByUnload(reason, pageUnload)) return;
    const message = reason instanceof Error ? reason.message : String(reason);
    autoBugReport('unhandledrejection', message, {
      stack: reason instanceof Error ? reason.stack : null,
    });
  });

}

/**
 * Begin streaming captured entries to the api. Until called, capture
 * happens locally only — useful in customer-facing sessions where we
 * don't want to ship logs back.
 *
 * Call once the user is signed in. It used to say "identified as staff
 * (system:observe)"; that privilege gate was removed from the endpoint on
 * 2026-08-12 because it is Admin-only and would have silently dropped every
 * Approver/Analyst/Viewer's telemetry. Authentication is still required.
 */
export function startStreaming(_sessionId: string = SESSION_ID): void {
  if (session) return;
  session = { id: _sessionId };
  if (flushTimer == null) {
    flushTimer = setInterval(() => void flushNow(), FLUSH_INTERVAL_MS);
  }
}

export function stopStreaming(): void {
  session = null;
  if (flushTimer != null) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  pending.length = 0;
}

/** Immediate snapshot of the buffer. Used by bug-report widget. */
export function getLogs(): ConsoleEntry[] {
  return buffer.slice();
}

export function getSessionId(): string {
  return SESSION_ID;
}

/**
 * Synchronously POST a one-off entry (bug report, DOM snapshot). Bypasses
 * the batch queue so it lands immediately even if streaming is paused.
 * Bug reports include a DOM snapshot which routinely exceeds the 64 KB
 * keepalive limit, so this path opts out of keepalive.
 */
export async function sendOneShot(entry: ConsoleEntry): Promise<void> {
  // Record in the recent-logs buffer so future bug reports include this
  // event. We deliberately do NOT push() to `pending` — that path is for the
  // periodic batch flush, and the entry would otherwise be sent twice (once
  // here, once by the next flush) — the two-identical-rows-per-click bug
  // (ISSUES.md id=25/27).
  buffer.push(entry);
  while (buffer.length > MAX_ENTRIES) buffer.shift();
  try {
    await postBatch([entry], { keepalive: false });
  } catch (err) {
    // Network failure, or a retryable status — fall back to the periodic flush
    // queue so the entry isn't lost. A NETWORK error (fetch rejects, no
    // HttpPostError) is always retryable; an HTTP status is retryable only per
    // isRetryable, so a 403/429 drops here rather than cycling forever.
    const retryable = !(err instanceof HttpPostError) || isRetryable(err.status);
    if (session && retryable) {
      pending.push(entry);
      while (pending.length > PENDING_CAP) pending.shift();
    }
    throw err;
  }
}

async function flushNow(): Promise<void> {
  if (!session || flushInFlight || pending.length === 0) return;
  flushInFlight = true;
  const batch = pending.splice(0, pending.length);
  try {
    await postBatch(batch);
  } catch (err) {
    // Re-queue on network failure or a retryable status, capped to
    // PENDING_CAP. The cap is what keeps a persistently-failing endpoint from
    // growing this unbounded in a long-lived admin session: past it, the batch
    // is dropped rather than accumulated.
    const retryable = !(err instanceof HttpPostError) || isRetryable(err.status);
    if (retryable && pending.length + batch.length <= PENDING_CAP) {
      pending.unshift(...batch);
    }
  } finally {
    flushInFlight = false;
  }
}

/**
 * The wire shape of one entry. Extracted so the batch can be MEASURED before
 * it is sent — the size that matters is the serialised body, not the entry
 * count, and nothing could see it while it was inline in `postBatch`.
 */
function wireEvent(e: ConsoleEntry): Record<string, unknown> {
  return {
    type: e.type, level: e.level, ts: e.ts,
    sessionId: SESSION_ID,
    route: typeof window !== 'undefined' ? window.location.pathname : null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    payload: e.payload ?? { message: e.message },
  };
}

/**
 * Drop diagnostic entries too old to be worth sending.
 *
 * ⚠️ `now` is INJECTED, not read from the environment. This is a DECISION — it
 * changes what gets sent — and the repo bans ambient time in a decision
 * (`.claude/rules/code/injected-clock.md`). The `ts` on each entry stays a
 * plain stamp; only the comparison instant is passed in, so a test can ask
 * "what would this do at a different moment?" and get an answer.
 *
 * A bug report is never dropped, whatever its age — see `NEVER_STALE`.
 *
 * Exported for tests, which must be able to name the instant.
 */
export function dropStale(
  entries: ConsoleEntry[],
  now: number,
  maxAgeMs = MAX_ENTRY_AGE_MS,
): ConsoleEntry[] {
  return entries.filter((e) => {
    if ((NEVER_STALE as readonly string[]).includes(e.type)) return true;
    // A future-dated entry (clock skew between tabs, a corrected system clock)
    // is kept: `now - ts` goes negative, which is not stale. Dropping it would
    // discard a fresh entry because a clock moved.
    return now - e.ts <= maxAgeMs;
  });
}

/**
 * Trim a batch to fit one POST, keeping the NEWEST entries.
 *
 * ⚠️ THIS DISCARDS DATA, on purpose (Craig, 2026-09-18). Chunking across
 * several POSTs was built first and removed: one bounded request is preferred
 * to many, and the price is the oldest diagnostics in an oversized batch.
 *
 * Two rules decide what survives:
 *
 *  1. **Bug reports first.** A person typed one, and it is the highest-signal
 *     row this layer ever carries. They are reserved before any console line
 *     competes for space, whatever their age or position.
 *  2. **Then newest-first.** The recent entries describe what just went wrong;
 *     a line from the start of the session rarely explains the end of it.
 *
 * Returns the survivors in their ORIGINAL order — the server stores what it is
 * given, and a reader expects a timeline, not a reversed one.
 *
 * ⚠️ If the reports alone exceed the cap, they still go: the newest that fit
 * are kept and the request is allowed to be large rather than silently losing
 * someone's report. The server's real limit is 8 MB, above this 6 MB cap, so
 * there is headroom for exactly this case.
 *
 * Exported for tests — the sizes involved are megabytes, which cannot be
 * driven through the public surface without a fixture that large.
 */
export function trimToBudget(
  entries: ConsoleEntry[],
  maxBytes = MAX_POST_BYTES,
): { kept: ConsoleEntry[]; discarded: number } {
  const size = (e: ConsoleEntry): number => JSON.stringify(wireEvent(e)).length + 1;

  const isReport = (e: ConsoleEntry): boolean =>
    (NEVER_STALE as readonly string[]).includes(e.type);

  // Reports are reserved first, newest-first among themselves so that an
  // over-cap pile of reports still keeps the most recent.
  const kept = new Set<ConsoleEntry>();
  let bytes = 0;
  for (const e of [...entries].filter(isReport).reverse()) {
    kept.add(e);
    bytes += size(e);
  }

  // Then everything else, newest-first, while there is room.
  for (const e of [...entries].reverse()) {
    if (kept.has(e)) continue;
    const s = size(e);
    if (bytes + s > maxBytes) continue;   // skip, don't break: a later (older)
    kept.add(e);                          // entry may still be small enough
    bytes += s;
  }

  return {
    kept: entries.filter((e) => kept.has(e)),   // original order restored
    discarded: entries.length - kept.size,
  };
}

async function postBatch(
  entries: ConsoleEntry[],
  opts: { keepalive?: boolean } = { keepalive: true },
): Promise<void> {
  // Age first, then size. Order matters: filtering stale entries often removes
  // the reason a batch was oversized at all, so fewer entries are discarded by
  // the cap below than would be otherwise.
  const fresh = dropStale(entries, Date.now());
  if (fresh.length === 0) return;

  // ONE request, bounded. Anything past the cap is discarded, newest kept.
  const { kept, discarded } = trimToBudget(fresh);
  if (kept.length === 0) return;
  // ⚠️ Record the gap IN THE BATCH, not via console.warn. The console is
  // wrapped by this very module (`installConsoleCapture`), so warning here
  // would push a new entry into the buffer during a flush — noise at best, and
  // a feedback loop at worst. A marker entry travels with the data instead, so
  // whoever reads the timeline can see that something is missing and how much.
  if (discarded > 0) {
    kept.push({
      type: 'capture_truncated',
      level: 'warn',
      ts: Date.now(),
      message: `${discarded} diagnostic entr${discarded === 1 ? 'y' : 'ies'} discarded — batch exceeded ${MAX_POST_BYTES} bytes`,
      payload: { discarded, maxBytes: MAX_POST_BYTES },
    });
  }

  try {
    await postOne(kept, opts);
  } catch (err) {
    // ⚠️ A 413 means the real limit is lower than our 6 MB cap — a proxy, or a
    // body that measured under it and arrived larger. `isRetryable` correctly
    // excludes 413 (re-queueing the SAME oversized body would fail identically
    // forever), but the consequence was that `flushNow` DROPPED the batch, and
    // with it any bug report a person had typed.
    //
    // So recover here instead of widening `isRetryable`: re-trim at half the
    // cap and send ONCE more. Still one request, not a chunked series — and
    // `trimToBudget` keeps the reports, so the retry loses console lines rather
    // than someone's report.
    //
    // If the retry cannot be smaller (a single entry, or one oversized report
    // that trimming refuses to drop), it is rethrown to the caller's normal
    // path rather than sent twice.
    if (err instanceof HttpPostError && err.status === 413 && kept.length > 1) {
      const retry = trimToBudget(kept, Math.floor(MAX_POST_BYTES / 2));
      if (retry.kept.length > 0 && retry.kept.length < kept.length) {
        await postOne(retry.kept, opts);
        return;
      }
    }
    throw err;
  }
}

async function postOne(
  entries: ConsoleEntry[],
  opts: { keepalive?: boolean } = { keepalive: true },
): Promise<void> {
  const body = JSON.stringify({ events: entries.map(wireEvent) });
  // keepalive caps the body at 64 KB per origin per document. We use it for
  // the periodic batch + the unload-time beacon (small payloads), but turn
  // it off for sendOneShot since bug reports carry a DOM snapshot that
  // routinely blows that limit.
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // Same device header `apiFetch` sends. It rides here too because THIS post
  // is often the first authenticated request a freshly-rotated token makes —
  // and the login row is written from whichever request the api sees first.
  // Without it, an unlucky ordering leaves the row's device column null.
  const device = clientDeviceHeader();
  if (device) headers[CLIENT_DEVICE_HEADER] = device;
  const token = await acquireBearer();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers,
    body,
    keepalive: opts.keepalive ?? true,
  });
  if (!res.ok) throw new HttpPostError(res.status);
}

/**
 * A non-2xx from the capture endpoint. Thrown so the two callers can apply
 * their existing re-queue paths — `flushNow` puts the batch back, `sendOneShot`
 * falls through to the periodic queue — rather than each re-deriving what to do.
 */
class HttpPostError extends Error {
  constructor(readonly status: number) {
    super(`client-events POST failed: ${status}`);
    this.name = 'HttpPostError';
  }
}

/**
 * Is this status worth retrying?
 *
 * ⚠️ 401 IS RETRYABLE, and that is the whole point of this change. The SPA
 * flushes buffered entries on a timer, and the first flush of a page load can
 * beat MSAL's token acquisition — so a perfectly healthy session emits a burst
 * of 401s in its first seconds. Observed on preview 2026-08-11: four 401s at
 * 14:54:36–37, twelve seconds BEFORE the app's own assets finished loading.
 * Those entries were dropped and never re-sent.
 *
 * What this replaced was worse than dropping. The old code read:
 *
 *     if (!res.ok && res.status !== 429 && res.status !== 401 && res.status !== 403) {
 *       // 401/403 = not staff → expected; stop streaming silently.
 *       if (res.status === 401 || res.status === 403) stopStreaming();
 *
 * — the outer condition excludes 401/403, so the inner branch was UNREACHABLE
 * and `stopStreaming()` never ran. Which was lucky: it nulls the session,
 * clears the flush timer and EMPTIES the pending queue, i.e. one transient auth
 * blip would have killed capture for the rest of the session and discarded
 * whatever was queued. The dead branch was a trap for anyone tidying it up.
 *
 * 403 is NOT retried: with the privilege gate removed from the endpoint
 * (system-monitor.ts), a 403 now means something genuinely wrong, and retrying
 * it forever would just be noise. 429 is not retried either — the server is
 * asking for less traffic, not more.
 */
function isRetryable(status: number): boolean {
  return status === 401 || status >= 500;
}

/* =========================================================== auto bug report */

/**
 * Auto-trigger path #2: any uncaught error / promise rejection / api
 * failure becomes a bug-report event WITH a DOM snapshot. Same shape as
 * a manual button-press; `payload.source` distinguishes the trigger.
 *
 * De-duped with a simple recent-message cache so a render loop hitting
 * the same error 200 times doesn't generate 200 reports.
 */
const recentlyReported = new Map<string, number>();
const DEDUPE_WINDOW_MS = 30_000;

export function autoBugReport(
  source: 'uncaught' | 'unhandledrejection' | 'api_error' | 'render_error',
  message: string,
  extra?: Record<string, unknown>,
): void {
  const key = `${source}:${message}`;
  const last = recentlyReported.get(key) ?? 0;
  const now = Date.now();
  const deduped = now - last < DEDUPE_WINDOW_MS;

  // Mint the bugId BEFORE the buffer tee so the detail row (`source`, which
  // carries the real message/status/stack) can be joined to its bug_report
  // and bug_report_snapshot. It used to be minted further down, after this
  // push, so the highest-signal row of the three was orphaned — leaving
  // triage to correlate on session_id + a timestamp window, which
  // mis-attributes exactly when it matters most (an error storm writes many
  // detail rows into one session inside the dedupe window).
  //
  // Suppressed repeats get no bugId: they are teed for a later manual
  // report's `recentLogs` but produce no bug_report of their own, so an id
  // here would dangle.
  const bugId = deduped
    ? null
    : (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${now}-${Math.random().toString(36).slice(2, 10)}`;

  // Always tee into the buffer so it's visible to a manual report later.
  // Stamp `message` into the payload too — the api-side log line extractor
  // reads payload.message and otherwise falls back to the row type, which
  // produces useless "uncaught: uncaught" lines in dev stdout.
  push({
    type: source, level: 'error', ts: now, message,
    payload: { ...(bugId ? { bugId } : {}), message, ...extra },
  });

  if (deduped) return;
  recentlyReported.set(key, now);

  const snapshot = (() => {
    try { return captureDomSnapshot(document.querySelector('main')); }
    catch { return null; }
  })();

  // Two-call split: details first (small, must land), snapshot second
  // (large, best-effort). Linked by bugId so the api can join them later.
  // Bypass the streaming gate for auto-reports — errors land regardless.
  // keepalive off on both because even the details row carries a stack.

  void postBatch([{
    type: 'bug_report',
    level: 'error',
    ts: now,
    message: `[auto] ${source}: ${message}`,
    payload: { bugId, source, error: { message, ...extra }, snapshotPending: !!snapshot },
  }], { keepalive: false }).catch(() => { /* swallow */ });

  if (snapshot) {
    void postBatch([{
      type: 'bug_report_snapshot',
      level: 'info',
      ts: now,
      message: `snapshot for ${bugId}`,
      payload: { bugId, snapshot },
    }], { keepalive: false }).catch(() => { /* swallow */ });
  }
}

/* =========================================================== DOM snapshot */

/**
 * Capture the current page's DOM + last N console lines. Caller passes the
 * element to snapshot — typically the active page's root, captured BEFORE
 * the bug-report modal mounts so the snapshot reflects what the user was
 * actually looking at.
 */
export function captureDomSnapshot(rootElement?: HTMLElement | null): {
  url:          string;
  route:        string;
  title:        string;
  viewport:     { width: number; height: number };
  userAgent:    string;
  domHtml:      string;     // capped — see implementation
  domBytes:     number;     // true pre-truncation size summed across regions
  domTruncated: boolean;    // true if any region was cut by the budget
  domRegions:   string[];   // which of main/header/nav made it in, in order
  recentLogs:   ConsoleEntry[];
  openDialogs:  { title: string }[];  // <dialog open> elements found in root
} {
  // 250 KB total across all captured regions. The server's MAX_PAYLOAD_BYTES
  // (apps/admin-api/src/routes/system-monitor.ts) must be >= this or it
  // re-truncates and this budget is silently meaningless.
  const MAX_DOM_BYTES = 250 * 1024;
  const root = rootElement ?? document.querySelector('main') ?? document.body;

  // Capture main content, then header, then side nav -- in that priority
  // order, sharing one budget (a "cascade"): each region takes what it needs
  // and the next gets whatever remains. A page whose main content alone fills
  // the budget therefore yields a COMPLETE main and no chrome, rather than
  // three half-truncated fragments.
  //
  // Header and nav are captured because a report about broken navigation
  // previously carried no evidence of it -- only <main> was ever captured.
  const regions: { name: string; el: Element | null }[] = [
    { name: 'main',   el: root ?? null },
    { name: 'header', el: document.querySelector('header') },
    { name: 'nav',    el: document.querySelector('nav[aria-label], aside nav, aside') },
  ];

  // ⚠️ Stamp `open` onto every genuinely-open <dialog> before serialising, and
  // take it off again below.
  //
  // `showModal()` does not write an `open` ATTRIBUTE — it sets internal browser
  // state and moves the element to the top layer. `dialog.open` (the property)
  // reports true; `outerHTML` comes back WITHOUT it. So a captured modal was
  // stored as a <dialog> with no `open`, which is `display: none` — the markup
  // sat in the capture and no viewer could ever show it.
  //
  // Found 2026-08-27, the first day a modal could be reported at all (the
  // in-modal trigger landed the same day). Tellingly, the one dialog that DID
  // carry the attribute was the bug-report form itself, caught mid-mount after
  // React had written it and before showModal() ran — the same distinction seen
  // from the other side.
  //
  // The attribute is removed again once the regions are serialised. `open` on a
  // live modal is not inert decoration, and leaving one behind would alter the
  // page the operator is still looking at.
  const stampedDialogs: HTMLDialogElement[] = [];
  for (const el of Array.from(document.querySelectorAll('dialog'))) {
    const dialog = el as HTMLDialogElement;
    if (dialog.open && !dialog.hasAttribute('open')) {
      dialog.setAttribute('open', '');
      stampedDialogs.push(dialog);
    }
  }

  const parts: string[] = [];
  const captured: string[] = [];
  let budget = MAX_DOM_BYTES;
  let fullLength = 0;   // true pre-truncation size across all regions
  let truncated = false;

  for (const { name, el } of regions) {
    if (!el) continue;
    // Skip a region already nested inside one we captured (e.g. a <nav> that
    // lives inside <main>), so it isn't stored twice and charged twice.
    if (parts.length > 0 && regions.some((r) => r.el && r.el !== el && r.el.contains(el))) continue;
    const html = (el as HTMLElement).outerHTML ?? '';
    fullLength += html.length;
    if (budget <= 0) { truncated = true; continue; }
    if (html.length > budget) {
      parts.push(`<!-- region:${name} (truncated) -->\n${html.slice(0, budget)}\n<!-- truncated -->`);
      captured.push(`${name} (partial)`);
      budget = 0;
      truncated = true;
    } else {
      parts.push(`<!-- region:${name} -->\n${html}`);
      captured.push(name);
      budget -= html.length;
    }
  }

  const domHtml = parts.join('\n');

  // Native <dialog> opened via showModal() lives at its React tree location
  // but is rendered to the browser's top layer. Enumerate any open ones so the
  // user (and triage) can see at a glance which page modal was open at click
  // time.
  //
  // ⚠️ Queried from `document`, NOT from `root`. This used to scan the captured
  // root, which is `<main>` — and the app's modals are mounted by page
  // components OUTSIDE `<main>`, so the list came back empty every time. The
  // field shipped, was never populated, and read as "no modal was open" rather
  // than "we did not look in the right place". Found 2026-08-26 while giving
  // modals their own bug-report trigger, which is what finally makes an open
  // dialog reportable at all.
  //
  // Consequence to keep in mind: a dialog outside `root` is NOT inside
  // `domHtml` either, so this list may name a modal whose markup was not
  // captured. Naming it is still strictly better than silence.
  const openDialogs = Array.from(document.querySelectorAll('dialog[open]'))
    .map((d) => ({ title: d.querySelector('h2')?.textContent?.trim() ?? d.getAttribute('aria-label') ?? '(untitled)' }));

  // ⚠️ Un-stamp LAST, and the order is load-bearing. `openDialogs` above
  // selects on `dialog[open]` — the ATTRIBUTE — so removing it any earlier
  // makes that list come back empty and the modal goes unnamed in triage.
  // Caught by console-capture.dialog.test.ts, which asserts both halves; the
  // first version of this fix un-stamped right after serialising and silently
  // broke the very field the capture exists to populate.
  for (const dialog of stampedDialogs) dialog.removeAttribute('open');

  return {
    url: window.location.href,
    route: window.location.pathname + window.location.search + window.location.hash,
    title: document.title,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    userAgent: navigator.userAgent,
    domHtml,
    domBytes: fullLength,
    domTruncated: truncated,
    domRegions: captured,
    recentLogs: getLogs(),
    openDialogs,
  };
}
